/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useClearAllFilters } from '../use-clear-all-filters';
import { isOverFiltered } from './is-over-filtered';

/**
 * Shown when applied filters reduce the result set to nothing. CRCB2B-348.
 *
 * ## Why this is separate from "no products found"
 *
 * An empty category and an over-filtered category are different situations with different
 * remedies: one is "there is nothing here", the other is "your filters excluded everything, and
 * here is how to undo that". The AC requires the second message specifically, and the design does
 * not show it — it is the one place on this ticket where the written criterion wins the tie-break,
 * because Tyler Trepton added it in direct answer to Zachary Trogdon's question rather than it
 * being pre-existing AC text.
 *
 * ## No compatibility model, deliberately
 *
 * The condition is the whole of it: zero results with filters applied. Nothing here knows or
 * needs to know which filter combinations are mutually exclusive.
 *
 * That is not a shortcut. SFCC evaluates refinements as **OR within one attribute and AND across
 * attributes**, which is what produces the empty set on its own. It is also what the design
 * depicts: the desktop frame shows two `eye-to-eye-stroke` values (`170x27.5` and `190x40`) applied
 * together with ten results — a combination that strict AND, which the AC's "all selected filters
 * shall be evaluated using AND logic" describes, would render as zero. So the AC's stated logic is
 * contradicted by its own design, the platform already behaves as the design shows, and building a
 * per-attribute compatibility engine in the application would compete with it for no gain.
 */
export default function InvalidFilterCombination({
    total,
    refineCount,
}: {
    /** Total results for the current query. */
    total: number;
    /**
     * How many refinements the SHOPPER applied. A route whose loader injects its own refinement
     * (the category listing pushes `cgid=<categoryId>` on every request) must exclude it first.
     */
    refineCount: number;
}): ReactElement | null {
    const { t } = useTranslation();
    /** Shared with the overlay's `Clear Filters` and the applied-filter row — see the hook's note. */
    const clearAllFilters = useClearAllFilters();

    if (!isOverFiltered(total, refineCount)) {
        return null;
    }

    return (
        // `title` is a PROP, not a child. `Alert` renders its header from `title` and treats
        // children as the content area below the divider, so an `<AlertTitle>` child left the
        // header row empty — an icon beside a blank box, then a stray hairline.
        <Alert
            data-slot="invalid-filter-combination"
            className="mb-8"
            title={t('categoryRefinements:invalidCombinationTitle')}>
            <AlertDescription>
                <p>{t('categoryRefinements:invalidCombinationBody')}</p>
                <Button variant="outline" onClick={clearAllFilters}>
                    {t('categoryRefinements:clearFilters')}
                </Button>
            </AlertDescription>
        </Alert>
    );
}
